import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, ChevronRight, MessageCircle, IndianRupee, Calendar, FileText } from "lucide-react";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20calculated%20my%20TDS%20penalty%20and%20need%20urgent%20help";

const ISSUE_TYPES = [
  { value: "LATE_DEDUCTION", label: "Late Deduction (1% per month interest)", section: "Section 201(1A)(i)" },
  { value: "LATE_PAYMENT", label: "Late Payment after Deduction (1.5% per month interest)", section: "Section 201(1A)(ii)" },
  { value: "LATE_FILING", label: "Late Filing of TDS Return (₹200/day penalty)", section: "Section 234E / 271H" },
];

const formatINR = (num) =>
  Math.round(num || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const TDSPenaltyCalculatorPage = () => {
  const [tdsAmount, setTdsAmount] = useState(50000);
  const [issueType, setIssueType] = useState("LATE_PAYMENT");
  const [periodDelayed, setPeriodDelayed] = useState(2);

  useCanonical("/tds-penalty-calculator");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "TDS Penalty Calculator",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "All",
    "description": "Calculate TDS penalty for late deduction, late payment and late filing of TDS return as per Section 201(1A) and Section 234E.",
    "url": "https://wintrustsolutions.com/tds-penalty-calculator",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
  });

  const selectedIssue = ISSUE_TYPES.find((i) => i.value === issueType) || ISSUE_TYPES[0];

  const results = useMemo(() => {
    const tds = Math.max(0, Number(tdsAmount) || 0);
    const period = Math.max(0, Number(periodDelayed) || 0);

    let charge = 0;
    let minimumNote = "";

    if (issueType === "LATE_DEDUCTION") {
      // 1% per month, part month = full month
      charge = tds * 0.01 * Math.ceil(period);
    } else if (issueType === "LATE_PAYMENT") {
      // 1.5% per month, part month = full month
      charge = tds * 0.015 * Math.ceil(period);
    } else if (issueType === "LATE_FILING") {
      // ₹200/day, capped at TDS amount, Section 271H minimum ₹10,000
      charge = Math.min(200 * period, tds);
      if (charge < 10000 && period > 0) {
        minimumNote = "Section 271H prescribes minimum penalty of ₹10,000 for late filing beyond one year.";
      }
    }

    const total = tds + charge;

    return { charge, total, minimumNote };
  }, [tdsAmount, issueType, periodDelayed]);

  const periodLabel = issueType === "LATE_FILING" ? "Number of Days Delayed" : "Number of Months Delayed";

  return (
    <>
      <Helmet>
        <title>TDS Penalty Calculator — Late Deduction, Payment & Filing | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Calculate TDS penalty for late deduction (1%), late payment (1.5%) and late filing (₹200/day) instantly. Free calculator based on Section 201(1A) & 234E." />
      </Helmet>

      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">TDS Penalty Calculator</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-4" data-testid="tds-penalty-h1">
            TDS Penalty Calculator — Late Deduction, Payment & Filing
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            Calculate TDS interest and late filing penalty under Section 201(1A), Section 234E and Section 271H of the Income Tax Act, 1961.
          </p>
        </div>
      </section>

      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200 shadow-sm">
              <h2 className="text-2xl font-semibold text-navy mb-6 font-heading">Enter Details</h2>

              <div className="mb-6">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <IndianRupee size={16} /> TDS Amount
                </label>
                <input
                  type="number"
                  min={0}
                  value={tdsAmount}
                  onChange={(e) => setTdsAmount(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="tds-amount-input"
                />
              </div>

              <div className="mb-6">
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <FileText size={16} /> Issue Type
                </label>
                <select
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="issue-type-select"
                >
                  {ISSUE_TYPES.map((it) => (
                    <option key={it.value} value={it.value}>{it.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-navy font-semibold mb-2 flex items-center gap-2">
                  <Calendar size={16} /> {periodLabel}
                </label>
                <input
                  type="number"
                  min={0}
                  value={periodDelayed}
                  onChange={(e) => setPeriodDelayed(Math.max(0, Number(e.target.value)))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy font-medium focus:outline-none focus:border-gold"
                  data-testid="period-input"
                />
                {issueType !== "LATE_FILING" && (
                  <p className="text-xs text-slate-500 mt-1">Part of a month is treated as a full month.</p>
                )}
              </div>
            </div>

            <div className="space-y-4" data-testid="tds-penalty-results">
              <div className="bg-navy rounded-xl p-6 md:p-8 text-center">
                <p className="text-slate-400 text-sm mb-1">Total Payable (TDS + Penalty)</p>
                <p className="text-4xl md:text-5xl font-bold text-gold font-heading" data-testid="tds-total">
                  ₹{formatINR(results.total)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <p className="text-slate-500 text-xs mb-1">TDS Amount</p>
                  <p className="text-xl font-bold text-navy" data-testid="tds-base">₹{formatINR(tdsAmount)}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                  <p className="text-slate-500 text-xs mb-1">Interest / Penalty</p>
                  <p className="text-xl font-bold text-navy" data-testid="tds-charge">₹{formatINR(results.charge)}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-5 border border-slate-200 text-xs text-slate-600">
                <strong className="text-navy">Applicable Section:</strong> {selectedIssue.section}
              </div>

              {results.minimumNote && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                  {results.minimumNote}
                </div>
              )}
            </div>
          </div>

          <div className="mt-10 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 md:p-8" data-testid="amber-alert">
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={28} />
              <div className="flex-1">
                <p className="text-amber-900 font-semibold text-base md:text-lg mb-4">
                  TDS penalties compound quickly. Get expert help before your next assessment.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp"
                    data-testid="cta-whatsapp-btn"
                  >
                    <MessageCircle size={18} /> Fix TDS Issue — WhatsApp Us
                  </a>
                  <Link
                    to="/tds-filing"
                    className="text-navy font-semibold hover:text-gold transition-colors flex items-center gap-1"
                    data-testid="link-tds-filing"
                  >
                    View TDS Filing Services <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs text-slate-500 max-w-3xl">
            <strong>Disclaimer:</strong> Per Section 201(1A) and Section 234E of Income Tax Act. Consult a tax expert for case-specific assessment.
          </p>
        </div>
      </section>
    </>
  );
};

export default TDSPenaltyCalculatorPage;
